/**
 * Microsoft Entra ID sign-in.
 *
 * The important difference from the application this replaces: it requests an
 * **access token scoped to our own API** and sends that. The old client put the
 * *ID token* in the Authorization header, which the backend accepted. An ID token
 * authenticates a browser session to the client app; it is not addressed to the
 * API and is not an authorization credential. The new backend rejects any token
 * without an `scp` claim, so this is the only thing that works.
 *
 * That requires the app registration to expose an API scope. See
 * docs/ENTRA-SETUP.md.
 */

import {
  type AccountInfo,
  type Configuration,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";

export interface AuthConfig {
  clientId: string;
  authority: string;
  apiScope: string;
}

let instance: PublicClientApplication | null = null;
let config: AuthConfig | null = null;

export async function initAuth(next: AuthConfig): Promise<PublicClientApplication> {
  config = next;
  instance = new PublicClientApplication(buildConfiguration(next));
  await instance.initialize();
  // Completes a redirect sign-in if we came back from one; null otherwise.
  const result = await instance.handleRedirectPromise();
  const account = result?.account ?? instance.getAllAccounts()[0];
  if (account) {
    instance.setActiveAccount(account);
  }
  return instance;
}

function buildConfiguration({ clientId, authority }: AuthConfig): Configuration {
  return {
    auth: {
      clientId,
      authority,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // Session storage, not local: a shared plant terminal should not keep a
      // signed-in session after the browser closes.
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    },
  };
}

export function getInstance(): PublicClientApplication {
  if (!instance) throw new Error("auth not initialised");
  return instance;
}

export function getAccount(): AccountInfo | null {
  return instance?.getActiveAccount() ?? null;
}

/**
 * A valid access token for our API, refreshing silently when possible.
 *
 * MSAL caches and renews on its own, so this is called before every request
 * rather than a token being held in application state. The previous client kept
 * the token in its store and scheduled its own refresh timer, which is work the
 * library already does correctly.
 */
export async function getAccessToken(): Promise<string> {
  const app = getInstance();
  if (!config) throw new Error("auth not initialised");

  const account = app.getActiveAccount();
  if (!account) throw new NotSignedIn();

  try {
    const result = await app.acquireTokenSilent({
      account,
      scopes: [config.apiScope],
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Consent revoked, password changed, MFA due. Only a full sign-in fixes it.
      await app.acquireTokenRedirect({ scopes: [config.apiScope] });
      throw new NotSignedIn();
    }
    throw error;
  }
}

export async function signIn(): Promise<void> {
  if (!config) throw new Error("auth not initialised");
  await getInstance().loginRedirect({ scopes: [config.apiScope] });
}

export async function signOut(): Promise<void> {
  await getInstance().logoutRedirect();
}

export class NotSignedIn extends Error {
  constructor() {
    super("no active account");
    this.name = "NotSignedIn";
  }
}
